import { cn } from '@woonstad/helpers-utils';
import type { AriaAttributes, Ref, ReactElement } from 'react';
import { cloneElement, forwardRef, isValidElement } from 'react';

import { DirectionsArrowRight } from '../../icons/directions';
import type { SVGComponent } from '../../types/SVGComponent';
import { Tag, TagProps } from '../tag/tag';
import { Typography, type Variant as TypographyVariant } from '../typography/Typography';
import { BoxBase } from './BoxBase';

type Props = AriaAttributes & {
  children: ReactElement<any>;
  textHTML?: string;
  icon?: SVGComponent;
  image?: ReactElement<any>;
  subtext?: string | ReactElement<any>;
  tags?: Array<TagProps>;
  variant?: 'wide' | 'square';
  className?: string;
  forceEnabled?: boolean;
  testid?: string;
  showIcon?: boolean;
  actionIcon?: SVGComponent;
  /** Places the action arrow on the title row instead of vertically centered on the card. */
  actionIconAlign?: 'center' | 'title';
  titleVariant?: Extract<TypographyVariant, 'label-regular' | 'label-large' | 'title-body'>;
  /** Use enabled title colors on non-interactive cards (e.g. informational cards without a link). */
  emphasizeTitle?: boolean;
};

function CardLinkIcon({
  Icon,
  isSquare,
  isDisabled,
}: Readonly<{
  Icon: SVGComponent;
  isSquare: boolean;
  isDisabled: boolean;
}>) {
  return (
    <Icon
      className={cn(
        'mr-2 shrink-0',
        isSquare ? 'size-20' : 'size-12',
        isDisabled && 'opacity-50',
      )}
    />
  );
}

function CardLinkImage({
  image,
  isWide,
  isSquare,
  isDisabled,
}: Readonly<{
  image: ReactElement<any>;
  isWide: boolean;
  isSquare: boolean;
  isDisabled: boolean;
}>) {
  const clonedImage = cloneElement(image, {
    ...image.props,
    className: (image.props.className ?? '') + ' size-full object-cover',
  });

  return (
    <div
      className={cn(
        'shrink-0 overflow-hidden',
        isWide && 'mr-4 h-full w-20',
        isSquare && 'aspect-[5/4] w-full',
        isDisabled && 'opacity-50',
      )}
    >
      {clonedImage}
    </div>
  );
}

function CardLinkActionIcon({
  actionIcon: ActionIcon,
  hasImage,
  isWide,
  inline = false,
}: Readonly<{
  actionIcon?: SVGComponent;
  hasImage: boolean;
  isWide: boolean;
  inline?: boolean;
}>) {
  const RightIcon = ActionIcon ?? DirectionsArrowRight;
  return (
    <RightIcon
      className={cn(
        'size-5 shrink-0 text-textlink-enabled-on-light',
        !inline && 'ml-auto',
        !ActionIcon && 'transition-transform group-hover:translate-x-1',
        (hasImage && isWide && !inline) && 'mr-4',
      )}
    />
  );
}

function getBoxBaseClasses(
  isSquare: boolean,
  isWide: boolean,
  hasImage: boolean,
  isDisabled: boolean,
  actionIconAlign: Props['actionIconAlign'],
) {
  return cn(
    'flex size-full bg-surface-default',
    actionIconAlign === 'title' ? 'items-start' : 'items-center',
    (!hasImage && isSquare) && 'p-4',
    (!hasImage && isWide) && 'px-4',
    !isDisabled && 'hover:shadow-box-hover',
    isSquare && 'flex-col items-stretch',
  );
}

function CardLinkLabel({
  text,
  textHTML,
  isDisabled,
  isSquare,
  hasImage,
  subtext,
  titleVariant = 'label-regular',
  emphasizeTitle = false,
}: Readonly<{
  text: string;
  textHTML?: string;
  isDisabled: boolean;
  isSquare: boolean;
  hasImage: boolean;
  subtext?: string | ReactElement<any>;
  titleVariant?: Props['titleVariant'];
  emphasizeTitle?: boolean;
}>) {
  const showEnabledTitleStyle = !isDisabled || emphasizeTitle;

  return (
    <Typography
      variant={titleVariant}
      className={cn(
        showEnabledTitleStyle ? 'text-card-link-enabled-label' : 'text-card-link-disabled-label',
        !isDisabled && 'group-hover:text-card-link-enabled-hover-label',
        (isSquare && hasImage) && 'mt-4 w-full px-4',
        (isSquare && hasImage && subtext) && 'mb-1',
        (isSquare && hasImage && !subtext) && 'mb-4',
      )}
      type="p"
    >
      <span
        className={cn(
          'decoration-2',
          !isDisabled && 'group-hover:underline',
        )}
        dangerouslySetInnerHTML={textHTML ? { __html: text } : undefined}
      >
        {!textHTML ? text : undefined}
      </span>
    </Typography>
  );
}

function CardLinkTitleRow({
  text,
  textHTML,
  isDisabled,
  isSquare,
  hasImage,
  subtext,
  actionIcon,
  showActionIcon,
  titleVariant,
  emphasizeTitle,
}: Readonly<{
  text: string;
  textHTML?: string;
  isDisabled: boolean;
  isSquare: boolean;
  hasImage: boolean;
  subtext?: string | ReactElement<any>;
  actionIcon?: SVGComponent;
  showActionIcon: boolean;
  titleVariant?: Props['titleVariant'];
  emphasizeTitle?: boolean;
}>) {
  const label = (
    <CardLinkLabel
      text={text}
      textHTML={textHTML}
      isDisabled={isDisabled}
      isSquare={isSquare}
      hasImage={hasImage}
      subtext={subtext}
      titleVariant={titleVariant}
      emphasizeTitle={emphasizeTitle}
    />
  );

  if (!showActionIcon) return label;

  return (
    <div className="flex w-full items-center gap-2">
      <div className="min-w-0 flex-1">
        {label}
      </div>
      <CardLinkActionIcon
        actionIcon={actionIcon}
        hasImage={hasImage}
        isWide
        inline
      />
    </div>
  );
}

function CardLinkSubtext({
  subtext,
  isDisabled,
  isSquare,
  hasImage,
}: Readonly<{
  subtext?: string | ReactElement<any>;
  isDisabled: boolean;
  isSquare: boolean;
  hasImage: boolean;
}>) {
  if (!subtext) return null;

  if (typeof subtext !== 'string') return subtext;

  return (
    <Typography
      className={cn(
        'text-left',
        isDisabled ? 'text-card-link-disabled-sublabel' : 'text-card-link-enabled-sublabel',
        (isSquare && hasImage) && 'mb-4 w-full px-4',
      )}
      variant="content-body"
    >
      {subtext}
    </Typography>
  );
}

/**
 * Goal
 * - Provide a way to create links that stands out.
 * - CardLink supports extra data to accompany a link, such as an icon, picure, sublabel or tag.
 *
 * Variants
 * - Wide, square.
 * - With an icon, picture or no visual.
 * - Depending on context, decide which variant is best.
 * - A common practice is to use the square variant on desktop and the mobile variant on small devices to save screen real estate. This is not mandatory though.
 *
 * States
 * - Enabled, hover, disabled.
 * - Note that disabled state is mostly used in dashboard contexts.
 *
 * Alternatives
 * - TextLink
 * - CardExpander
 */
export const CardLink = forwardRef((
  {
    children,
    textHTML,
    icon: Icon,
    image,
    subtext,
    tags,
    variant = 'wide',
    forceEnabled = false,
    className,
    testid,
    showIcon = true,
    actionIcon,
    actionIconAlign = 'center',
    titleVariant = 'label-regular',
    emphasizeTitle = false,
  }: Props,
  ref?: Ref<HTMLDivElement>,
) => {
  const text = textHTML || (isValidElement(children) ? (children.props as { children?: any }).children : '');
  const isDisabled = (children.type !== 'button' && !(children?.props as { href?: string })?.href) && !forceEnabled;

  const isWide = variant === 'wide';
  const isSquare = variant === 'square';
  const hasImage = image !== undefined;

  const showActionIcon = !isDisabled && !isSquare;
  const isTitleActionIcon = showActionIcon && actionIconAlign === 'title';
  const isCenterActionIcon = showActionIcon && actionIconAlign === 'center';

  return (
    <children.type
      {...(children.props as any)}
      className={cn(
        'group size-full',
        className,
      )}
    >
      <BoxBase
        ref={ref}
        className={getBoxBaseClasses(isSquare, isWide, hasImage, isDisabled, actionIconAlign)}
        noShadow={isDisabled}
        data-testid={testid}
      >
        {showIcon && !hasImage && Icon && (
          <CardLinkIcon Icon={Icon} isSquare={isSquare} isDisabled={isDisabled} />
        )}

        {showIcon && !Icon && hasImage && (
          <CardLinkImage image={image} isWide={isWide} isSquare={isSquare} isDisabled={isDisabled} />
        )}

        <InfoWrapper variant={variant} actionIconAlign={actionIconAlign}>
          {tags && tags.length > 0 && (
            <div
              className={cn(
                'mb-1 flex flex-wrap gap-2',
                (isSquare && !hasImage) && '-order-1 justify-around',
                (isSquare && hasImage) && 'absolute self-start p-2',
              )}
            >
              {tags.map((tag) => (
                <Tag
                  key={tag.children?.toString()}
                  {...tag}
                />
              ))}
            </div>
          )}

          <div className={cn(actionIconAlign === 'title' && subtext && 'mb-4')}>
            {isTitleActionIcon ? (
              <CardLinkTitleRow
                text={text}
                textHTML={textHTML}
                isDisabled={isDisabled}
                isSquare={isSquare}
                hasImage={hasImage}
                subtext={subtext}
                actionIcon={actionIcon}
                showActionIcon={showActionIcon}
                titleVariant={titleVariant}
                emphasizeTitle={emphasizeTitle}
              />
            ) : (
              <CardLinkLabel
                text={text}
                textHTML={textHTML}
                isDisabled={isDisabled}
                isSquare={isSquare}
                hasImage={hasImage}
                subtext={subtext}
                titleVariant={titleVariant}
                emphasizeTitle={emphasizeTitle}
              />
            )}
          </div>

          <CardLinkSubtext
            subtext={subtext}
            isDisabled={isDisabled}
            isSquare={isSquare}
            hasImage={hasImage}
          />

        </InfoWrapper>

        {isCenterActionIcon && (
          <CardLinkActionIcon actionIcon={actionIcon} hasImage={hasImage} isWide={isWide} />
        )}
      </BoxBase>
    </children.type>
  );
});

CardLink.displayName = 'CardLink';

// Need for this extra div is due to the placement of the tags on the square variant.
// They need to be inside the parent container on the square variant, but outside on the wide variant.
function InfoWrapper({
  variant,
  actionIconAlign,
  children,
}: {
  variant: Props['variant'],
  actionIconAlign: Props['actionIconAlign'],
  children: React.ReactNode
}) {
  if (variant === 'wide') {
    return (
      <div
        className={cn(
          'flex flex-col py-4 text-left',
          actionIconAlign === 'title' ? 'min-w-0 flex-1' : 'mr-4',
        )}
      >
        {children}
      </div>
    );
  }

  return children;
}
